export interface DateParts {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number; // 0-23
  minute: number;
  second: number;
  millisecond: number;
  weekday: number; // 0 = Sunday
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAY_INDEX: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const TOKEN = /\[([^\]]*)\]|YYYY|YY|MMMM|MMM|MM|M|DD|D|dddd|ddd|HH|H|hh|h|mm|m|ss|s|SSS|A|a/g;

const pad = (n: number, width = 2) => String(n).padStart(width, '0');

interface LocaleNames {
  months: string[];
  monthsShort: string[];
  weekdays: string[];
  weekdaysShort: string[];
  am: string;
  pm: string;
}

const ENGLISH: LocaleNames = {
  months: MONTHS,
  monthsShort: MONTHS.map((m) => m.slice(0, 3)),
  weekdays: WEEKDAYS,
  weekdaysShort: WEEKDAYS.map((d) => d.slice(0, 3)),
  am: 'AM',
  pm: 'PM',
};

const localeNames = new Map<string, LocaleNames>();

/** True for a BCP 47 tag the browser has date names for ('' = English). */
export function isValidLocale(locale: string): boolean {
  if (!locale) return true;
  try {
    return Intl.DateTimeFormat.supportedLocalesOf([locale]).length > 0;
  } catch {
    return false;
  }
}

/**
 * Month, weekday and day-period names in `locale`, from Intl so they follow
 * the browser's CLDR data. '' and invalid tags give English.
 */
export function namesFor(locale: string): LocaleNames {
  if (!locale || !isValidLocale(locale)) return ENGLISH;
  let names = localeNames.get(locale);
  if (names) return names;
  const part = (opts: Intl.DateTimeFormatOptions, date: Date, type: Intl.DateTimeFormatPartTypes) =>
    new Intl.DateTimeFormat(locale, { ...opts, timeZone: 'UTC' }).formatToParts(date).find((p) => p.type === type)?.value ?? '';
  // Standalone month names ("octubre", not "de octubre"): format the month alone.
  const months = MONTHS.map((_, i) => new Intl.DateTimeFormat(locale, { month: 'long', timeZone: 'UTC' }).format(Date.UTC(2026, i, 15)));
  const monthsShort = MONTHS.map((_, i) => new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' }).format(Date.UTC(2026, i, 15)));
  // 2026-02-01 is a Sunday.
  const weekdays = WEEKDAYS.map((_, i) => new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(Date.UTC(2026, 1, 1 + i)));
  const weekdaysShort = WEEKDAYS.map((_, i) => new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(Date.UTC(2026, 1, 1 + i)));
  const am = part({ hour: 'numeric', hour12: true }, new Date(Date.UTC(2026, 0, 1, 9)), 'dayPeriod') || 'AM';
  const pm = part({ hour: 'numeric', hour12: true }, new Date(Date.UTC(2026, 0, 1, 21)), 'dayPeriod') || 'PM';
  names = { months, monthsShort, weekdays, weekdaysShort, am, pm };
  localeNames.set(locale, names);
  return names;
}

/**
 * Formats with Moment-style tokens: YYYY YY MMMM MMM MM M DD D dddd ddd HH H hh h mm m ss s SSS A a.
 * Text in [brackets] is copied verbatim. Names and AM/PM follow `locale` ('' = English).
 */
export function formatDateParts(p: DateParts, format: string, locale = ''): string {
  const h12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  const n = namesFor(locale);
  const period = p.hour < 12 ? n.am : n.pm;
  return format.replace(TOKEN, (token, literal?: string) => {
    if (literal !== undefined) return literal;
    switch (token) {
      case 'YYYY': return pad(p.year, 4);
      case 'YY': return pad(p.year % 100);
      case 'MMMM': return n.months[p.month - 1];
      case 'MMM': return n.monthsShort[p.month - 1];
      case 'MM': return pad(p.month);
      case 'M': return String(p.month);
      case 'DD': return pad(p.day);
      case 'D': return String(p.day);
      case 'dddd': return n.weekdays[p.weekday];
      case 'ddd': return n.weekdaysShort[p.weekday];
      case 'HH': return pad(p.hour);
      case 'H': return String(p.hour);
      case 'hh': return pad(h12);
      case 'h': return String(h12);
      case 'mm': return pad(p.minute);
      case 'm': return String(p.minute);
      case 'ss': return pad(p.second);
      case 's': return String(p.second);
      case 'SSS': return pad(p.millisecond, 3);
      case 'A': return period.toLocaleUpperCase(locale || undefined);
      case 'a': return period.toLocaleLowerCase(locale || undefined);
      default: return token;
    }
  });
}

/** True when `format` shows seconds or finer, i.e. the text changes at least once a second. */
export function formatShowsSeconds(format: string): boolean {
  return /s|S/.test(format.replace(/\[[^\]]*\]/g, ''));
}

const zoneFormatters = new Map<string, Intl.DateTimeFormat>();

export function isValidTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** Wall-clock fields of `date` in `timeZone` ('' = the browser's local zone). Throws RangeError for an invalid zone. */
export function getDateParts(date: Date, timeZone: string): DateParts {
  if (!timeZone) {
    return {
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      day: date.getDate(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds(),
      millisecond: date.getMilliseconds(),
      weekday: date.getDay(),
    };
  }
  let fmt = zoneFormatters.get(timeZone);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    });
    zoneFormatters.set(timeZone, fmt);
  }
  const v: Record<string, string> = {};
  for (const part of fmt.formatToParts(date)) v[part.type] = part.value;
  return {
    year: Number(v.year),
    month: Number(v.month),
    day: Number(v.day),
    hour: Number(v.hour) % 24,
    minute: Number(v.minute),
    second: Number(v.second),
    millisecond: date.getMilliseconds(),
    weekday: WEEKDAY_INDEX[v.weekday] ?? 0,
  };
}

export function formatClock(date: Date, format: string, timeZone: string, locale = ''): string {
  return formatDateParts(getDateParts(date, timeZone), format, locale);
}

/** Every IANA zone the browser knows, or an empty list on browsers without Intl.supportedValuesOf. */
export function listTimeZones(): string[] {
  const fn = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf;
  try {
    return fn ? fn('timeZone') : [];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// Countdown
// ---------------------------------------------------------------------------

const DURATION_TOKEN = /\[([^\]]*)\]|DD|D|HH|H|mm|m|ss|s|SSS/g;

/**
 * Formats a remaining duration. Seconds are rounded up unless SSS is present,
 * so a timer reads 00:00:00 only once it has really finished. A unit wraps only
 * when the next larger one is in the format (no H: minutes reach 90; no D: hours pass 24).
 */
export function formatDuration(ms: number, format: string): string {
  const exact = format.includes('SSS');
  const clamped = Math.max(0, ms);
  const total = exact ? Math.floor(clamped) : Math.ceil(clamped / 1000) * 1000;
  const tokens = format.replace(/\[[^\]]*\]/g, '');
  const withDays = /D/.test(tokens);
  const withHours = /H/.test(tokens);
  const withMinutes = /m/.test(tokens);
  const totalSeconds = Math.floor(total / 1000);
  const days = Math.floor(totalSeconds / 86400);
  // A unit wraps only when the next larger unit is displayed; otherwise it carries the whole amount.
  const hours = withDays ? Math.floor(totalSeconds / 3600) % 24 : Math.floor(totalSeconds / 3600);
  const minutes = withHours ? Math.floor(totalSeconds / 60) % 60 : Math.floor(totalSeconds / 60);
  const seconds = withMinutes ? totalSeconds % 60 : totalSeconds;
  return format.replace(DURATION_TOKEN, (token, literal?: string) => {
    if (literal !== undefined) return literal;
    switch (token) {
      case 'DD': return pad(days);
      case 'D': return String(days);
      case 'HH': return pad(hours);
      case 'H': return String(hours);
      case 'mm': return pad(minutes);
      case 'm': return String(minutes);
      case 'ss': return pad(seconds);
      case 's': return String(seconds);
      case 'SSS': return pad(total % 1000, 3);
      default: return token;
    }
  });
}

export interface CountdownState {
  running: boolean;
  /** Wall-clock ms (Date.now()) when the current run started; meaningful only while running. */
  startedAt: number;
  /** Time left at `startedAt` (or now, when paused). */
  remainingAtStart: number;
}

export function resetCountdown(durationMs: number): CountdownState {
  return { running: false, startedAt: 0, remainingAtStart: Math.max(0, durationMs) };
}

/**
 * Wall-clock based so skipped frames, a hidden tab or a reload never make the
 * timer drift. With `loopMs` > 0 a finished run starts over at once.
 */
export function countdownRemaining(state: CountdownState, now: number, loopMs = 0): number {
  if (!state.running) return state.remainingAtStart;
  const left = state.remainingAtStart - (now - state.startedAt);
  if (left > 0) return left;
  if (loopMs > 0) return loopMs - (-left % loopMs);
  return 0;
}

/** Ran to zero and stays there (until reset or started again). */
export function countdownFinished(state: CountdownState, now: number, loopMs = 0): boolean {
  return state.running && loopMs <= 0 && countdownRemaining(state, now) <= 0;
}

/** Starts, resumes a paused run, or restarts a finished one from `durationMs`. */
export function startCountdown(state: CountdownState, now: number, durationMs = state.remainingAtStart): CountdownState {
  if (state.running && !countdownFinished(state, now)) return state;
  const from = !state.running && state.remainingAtStart > 0 ? state.remainingAtStart : durationMs;
  if (from <= 0) return state;
  return { running: true, startedAt: now, remainingAtStart: from };
}

/** Pausing a finished run keeps it finished, so the finished text stays on air. */
export function pauseCountdown(state: CountdownState, now: number, loopMs = 0): CountdownState {
  if (!state.running || countdownFinished(state, now, loopMs)) return state;
  return { running: false, startedAt: 0, remainingAtStart: countdownRemaining(state, now, loopMs) };
}

/** Run state as stored through SourceContext.saveRuntimeState. */
export function countdownToBag(state: CountdownState, durationMs: number): Record<string, number | boolean> {
  return { running: state.running, startedAtEpochMs: state.startedAt, remainingAtStartMs: state.remainingAtStart, durationMs };
}

/** The stored run state, or null when it is malformed or belongs to a different duration. */
export function countdownFromBag(bag: unknown, durationMs: number): CountdownState | null {
  if (!bag || typeof bag !== 'object') return null;
  const b = bag as Record<string, unknown>;
  const finite = (v: unknown) => typeof v === 'number' && Number.isFinite(v);
  if (typeof b.running !== 'boolean' || !finite(b.startedAtEpochMs) || !finite(b.remainingAtStartMs) || b.durationMs !== durationMs) return null;
  const remaining = Math.min(Math.max(0, b.remainingAtStartMs as number), durationMs);
  return { running: b.running, startedAt: b.running ? (b.startedAtEpochMs as number) : 0, remainingAtStart: remaining };
}

/** Remaining ms until a target instant, or null if `iso` is not a valid date. */
export function remainingUntil(iso: string, now: number): number | null {
  const target = Date.parse(iso);
  return Number.isNaN(target) ? null : Math.max(0, target - now);
}
